import {
  createAsyncThunk,
  createSlice,
  appLogout,
  rootReducer,
  persistReducer,
  createSelector,
} from '@bonanza-social/toolkit/state';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { StatusPerksService } from '../../services';
import {
  PerksConfigState,
  StatusPerkListItem,
  StatusPerkType,
} from './perksConfig.types';
import { StatusType } from '../status/status.types';
import {
  buildInitialState,
  groupPerksByType,
  getPerkUpgradeType,
} from './perksConfig.utils';

const initialState: PerksConfigState = {
  data: buildInitialState(),
  dataState: 'idle',
};

export const fetchPerksConfig = createAsyncThunk(
  'perksConfig/fetch',
  async () => await StatusPerksService.getPerks(),
);

const perksConfig = createSlice({
  name: 'perksConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPerksConfig.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchPerksConfig.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchPerksConfig.fulfilled, (state, { payload }) => {
      payload.statusPerks.forEach((item) => {
        state.data[item.status] = item;
      });
      state.dataState = 'fulfilled';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

const persistConfig = {
  key: 'perksConfig',
  storage: AsyncStorage,
  whitelist: ['data'],
};

const persistedReducer = persistReducer(persistConfig, perksConfig.reducer);

export const withPerksConfig = rootReducer.inject({
  reducerPath: 'perksConfig',
  reducer: persistedReducer,
});

export const selectPerksConfig = withPerksConfig.selector(
  (state) => state.perksConfig.data,
);

export const selectPerksConfigDataState = withPerksConfig.selector(
  (state) => state.perksConfig.dataState,
);

export const selectPerksListForStatus = createSelector(
  [
    (_, { currentStatus }: { currentStatus: StatusType }) => currentStatus,
    (_, { nextStatus }: { nextStatus?: StatusType }) => nextStatus,
    selectPerksConfig,
  ],
  (currentStatus, nextStatus, config): StatusPerkListItem[] | undefined => {
    if (currentStatus === 'Member') {
      const seenPerks: Partial<Record<StatusPerkType, boolean>> = {};

      return Object.values(config).reduce((acc, item) => {
        for (const perk of item.perks) {
          if (!seenPerks[perk.type]) {
            seenPerks[perk.type] = true;

            acc.push({
              perkType: perk.type,
              currentStatusPerkAmount: perk.value,
              nextStatusPerkAmount: perk.value,
              upgradeType: 'ReachNextStatusToUnlock',
              currentStatus: item.status,
              nextStatus: item.status,
            });
          }
        }

        return acc;
      }, [] as StatusPerkListItem[]);
    }

    const currentStatusPerks = config[currentStatus].perks;
    const nextStatusPerks = nextStatus ? config[nextStatus].perks : [];
    const currentStatusPerksMap = groupPerksByType(currentStatusPerks);

    if (!nextStatus) {
      return currentStatusPerks.map((perk) => ({
        perkType: perk.type,
        currentStatusPerkAmount: perk.value,
        upgradeType: 'MaxLevel',
        currentStatus,
      }));
    }

    return nextStatusPerks.map((perk) => {
      const currentStatusPerk = currentStatusPerksMap[perk.type];
      const upgradeType = getPerkUpgradeType({
        currentStatusPerk: currentStatusPerksMap[perk.type],
        nextStatusPerk: perk,
      });

      // if the perk hasn't been unlocked, it doesn't exist in the current status perks array
      // so we get it's value from the next status perks array
      const currentStatusPerkAmount =
        upgradeType === 'ReachNextStatusToUnlock'
          ? perk.value
          : currentStatusPerk?.value;

      return {
        perkType: perk.type,
        currentStatusPerkAmount,
        nextStatusPerkAmount: perk.value,
        upgradeType,
        currentStatus,
        nextStatus,
      };
    });
  },
);

declare global {
  interface LazyLoadedSlices {
    perksConfig: ReturnType<typeof persistedReducer>;
  }
}
